import {
  MutableRef,
  useLayoutEffect,
  useRef,
} from 'preact/hooks'

/**
 * Suppress the warning when using useLayoutEffect with SSR. (https://reactjs.org/link/uselayouteffect-ssr)
 * Make use of useInsertionEffect if available.
 */
const useBrowserEffect = typeof window !== "undefined" ? useLayoutEffect : () => { /* do nothing */ };

/**
 * Similar to useCallback, with a few subtle differences:
 * - The returned function is a stable reference, and will always be the same between renders
 * - No dependency lists required
 * - Properties or state accessed within the callback will always be "current"
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function useEvent<T extends Function>(callback: T): T {
  // Keep track of the latest callback:
  const latestRef = useRef<T>(useEvent_shouldNotBeInvokedBeforeMount as unknown as T)
  useBrowserEffect(() => {
    latestRef.current = callback;
  }, [callback]);

  // Create a stable callback that always calls the latest callback:
  // using useRef instead of useCallback avoids creating and empty array on every render
  const stableRef = useRef<T>(null) as MutableRef<T>;
  if (stableRef.current === null) {
    stableRef.current = function () {
      // eslint-disable-next-line prefer-rest-params
      return latestRef.current.apply(this, arguments)
    } as unknown as T
  }

  return stableRef.current;
}

/**
 * Render methods should be pure, especially when concurrency is used,
 * so we will throw this error if the callback is called while rendering.
 */
function useEvent_shouldNotBeInvokedBeforeMount() {
  throw new Error(
    "INVALID_USEEVENT_INVOCATION: the callback from useEvent cannot be invoked before the component has mounted."
  );
}

export default useEvent;
